"use client";

import React, { useState, useEffect } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { MessageDto, GroupMemberDto, MessageType, UserType } from '@/types';
import { formatTimestamp, formatFileSize, avatarDefault, getAvatarColor, getInitials } from '@/lib/utils';
import { Image, Video, FileText, Download, Phone, Mail, Star } from 'lucide-react';
import styles from './RightPanel.module.css';

export default function RightPanel() {
    const {
        selectedRoom,
        loadMessages,
        openImageViewer,
        openVideoViewer,
        userType
    } = useChatContext();

    const { t } = useLanguage();

    const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({
        media: false,
        images: true,
        documents: false
    });
    const [mediaFiles, setMediaFiles] = useState<MessageDto[]>([]);
    const [imageFiles, setImageFiles] = useState<MessageDto[]>([]);
    const [documentFiles, setDocumentFiles] = useState<MessageDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const attachmentSummary = [
        { id: 'media', label: t.videos, count: mediaFiles.length, icon: Video },
        { id: 'images', label: t.images, count: imageFiles.length, icon: Image },
        { id: 'documents', label: t.files, count: documentFiles.length, icon: FileText }
    ];

    // Selected user for popup
    const [selectedUser, setSelectedUser] = useState<GroupMemberDto | null>(null);

    useEffect(() => {
        if (userType === UserType.Customer) {
            setSelectedUser(null);
        }
    }, [userType]);

    // Helper to parse album URLs from text format [ALBUM:url1|url2|url3]
    const parseAlbumUrls = (text: string): string[] => {
        if (text.startsWith('[ALBUM:') && text.endsWith(']')) {
            const urlsString = text.substring(7, text.length - 1);
            return urlsString.split('|').filter((url) => url.trim() !== '');
        }
        return [];
    };

    // Load all messages to get attachments
    useEffect(() => {
        if (!selectedRoom?.conversationID) {
            setMediaFiles([]);
            setImageFiles([]);
            setDocumentFiles([]);
            return;
        }

        const loadAllMessages = async () => {
            setIsLoading(true);
            let allMessages: MessageDto[] = [];
            let oldestMessageID = '';
            let hasMore = true;

            while (hasMore) {
                const batch = await loadMessages(selectedRoom.conversationID || '', oldestMessageID, 100);
                if (batch.length > 0) {
                    allMessages = [...batch, ...allMessages];
                    oldestMessageID = batch[0].clientMsgID;
                    if (batch.length < 100) hasMore = false;
                } else {
                    hasMore = false;
                }
            }

            // Filter videos - only include if videoElem exists
            setMediaFiles(allMessages.filter(m =>
                m.contentType === MessageType.VideoMessage && m.videoElem?.videoUrl
            ));

            // Filter images - include both PictureMessages and Album messages
            const pictureMessages = allMessages.filter(m =>
                m.contentType === MessageType.PictureMessage && m.pictureElem?.sourcePicture?.url
            );

            // Extract images from Album messages (TextMessages with [ALBUM:...] format)
            const albumImages: MessageDto[] = [];
            allMessages.forEach(m => {
                if (m.contentType === MessageType.TextMessage && m.textElem?.content) {
                    const albumUrls = parseAlbumUrls(m.textElem.content);
                    albumUrls.forEach((url, index) => {
                        // Create a synthetic message for each album image
                        albumImages.push({
                            ...m,
                            clientMsgID: `${m.clientMsgID}_album_${index}`,
                            pictureElem: {
                                sourcePicture: { url }
                            }
                        });
                    });
                }
            });

            setImageFiles([...pictureMessages, ...albumImages]);

            // Filter files - only include if fileElem exists
            setDocumentFiles(allMessages.filter(m =>
                m.contentType === MessageType.FileMessage && m.fileElem?.sourceUrl
            ));

            setIsLoading(false);
        };

        loadAllMessages();
    }, [selectedRoom?.conversationID, loadMessages]);

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const viewImage = (image: MessageDto) => {
        if (!image.pictureElem?.sourcePicture?.url) return;
        const allUrls = imageFiles
            .filter(m => m.pictureElem?.sourcePicture?.url)
            .map(m => m.pictureElem!.sourcePicture.url);
        const index = allUrls.indexOf(image.pictureElem.sourcePicture.url);
        openImageViewer(allUrls, index >= 0 ? index : 0);
    };

    const viewMedia = (media: MessageDto) => {
        if (!media.videoElem?.videoUrl) return;
        const allUrls = mediaFiles
            .filter(m => m.videoElem?.videoUrl)
            .map(m => m.videoElem!.videoUrl);
        const index = allUrls.indexOf(media.videoElem.videoUrl);
        openVideoViewer(allUrls, index >= 0 ? index : 0);
    };

    const downloadFile = (doc: MessageDto) => {
        if (!doc.fileElem) return;
        const link = document.createElement('a');
        link.href = doc.fileElem.sourceUrl;
        link.download = doc.fileElem.fileName;
        link.click();
    };

    if (!selectedRoom) return null;

    return (
        <div className={styles.rightPanel}>
            {/* Header */}
            <div className={styles.ticketHeader}>
                <div className={styles.headerContent}>
                    <Star size={18} className={styles.starIcon} />
                    <div>
                        <h3>{t.chatDetails}</h3>
                    </div>
                </div>
            </div>

            {/* User List Section */}
            {userType !== UserType.Customer && selectedRoom.members && selectedRoom.members.length > 0 && (
                <div className={styles.usersSection}>
                    {selectedRoom.members.map(user => (
                        <div key={user.userID} className={styles.userItem}>
                            <div className={styles.userAvatar}>
                                {user.faceURL ? (
                                    <img src={user.faceURL} alt={user.nickname || user.userID} />
                                ) : (
                                    <div className={styles.avatarInitials} style={{ backgroundColor: getAvatarColor(user.nickname || user.userID) }}>
                                        {getInitials(user.nickname || user.userID)}
                                    </div>
                                )}
                            </div>
                            <div className={styles.userInfo}>
                                <div className={styles.userEmail}>{user.email || user.userID}</div>
                                <div className={styles.userName}>{user.nickname || user.userID}</div>
                            </div>
                            <button className={styles.userMenu} onClick={() => setSelectedUser(user)}>
                                ⋯
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.summaryGrid}>
                {attachmentSummary.map(card => {
                    const Icon = card.icon;
                    return (
                        <div key={card.id} className={styles.summaryCard}>
                            <div className={styles.summaryIcon}> <Icon size={18} /> </div>
                            <div className={styles.summaryContent}>
                                <span>{card.label}</span>
                                <strong>{card.count}</strong>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Attachments Section */}
            <div className={styles.attachmentsSection}>
                {/* Media Files */}
                <div className={styles.attachmentCategory}>
                    <div className={styles.categoryHeader} onClick={() => toggleCategory('media')}>
                        <div className={styles.attachmentItem}>
                            <div className={styles.attachmentIcon}>
                                <Video size={20} />
                            </div>
                            <span>{t.videos} ({mediaFiles.length})</span>
                        </div>
                        <span className={`${styles.expandIcon} ${expandedCategories['media'] ? styles.expanded : ''}`}>
                            ▼
                        </span>
                    </div>

                    {expandedCategories['media'] && (
                        <div className={styles.categoryContent}>
                            {mediaFiles.length > 0 ? (
                                <div className={styles.mediaGrid}>
                                    {mediaFiles.map(media => (
                                        <div key={media.clientMsgID} className={styles.mediaItem} onClick={() => viewMedia(media)}>
                                            <div className={styles.mediaThumbnail}>
                                                {media.videoElem?.snapshotUrl ? (
                                                    <>
                                                        <img src={media.videoElem.snapshotUrl} alt="Video" />
                                                        <div className={styles.videoOverlay}>
                                                            <span className={styles.playBtn}>▶</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className={styles.videoOverlay} style={{ background: '#374151' }}>
                                                        <Video size={20} color="#fff" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.mediaInfo}>
                                                <div className={styles.mediaName}>{media.fileName || 'Video'}</div>
                                                <div className={styles.mediaSize}>
                                                    {formatFileSize(media.videoElem?.videoSize || 0)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <p>{t.noVideosShared}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Images */}
                <div className={styles.attachmentCategory}>
                    <div className={styles.categoryHeader} onClick={() => toggleCategory('images')}>
                        <div className={styles.attachmentItem}>
                            <div className={styles.attachmentIcon}>
                                <Image size={20} />
                            </div>
                            <span>{t.images} ({imageFiles.length})</span>
                        </div>
                        <span className={`${styles.expandIcon} ${expandedCategories['images'] ? styles.expanded : ''}`}>
                            ▼
                        </span>
                    </div>

                    {expandedCategories['images'] && (
                        <div className={styles.categoryContent}>
                            {imageFiles.length > 0 ? (
                                <div className={styles.imageGrid}>
                                    {imageFiles.filter(img => img.pictureElem?.sourcePicture?.url).map(image => (
                                        <div key={image.clientMsgID} className={styles.imageItem} onClick={() => viewImage(image)}>
                                            <img src={image.pictureElem?.sourcePicture?.url} alt="Images" />
                                            <div className={styles.imageOverlay}>
                                                <div className={styles.imageName}>{image.fileName || 'Image'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <p>{t.noImagesShared}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Documents */}
                <div className={styles.attachmentCategory}>
                    <div className={styles.categoryHeader} onClick={() => toggleCategory('documents')}>
                        <div className={styles.attachmentItem}>
                            <div className={styles.attachmentIcon}>
                                <FileText size={20} />
                            </div>
                            <span>{t.files} ({documentFiles.length})</span>
                        </div>
                        <span className={`${styles.expandIcon} ${expandedCategories['documents'] ? styles.expanded : ''}`}>
                            ▼
                        </span>
                    </div>

                    {expandedCategories['documents'] && (
                        <div className={styles.categoryContent}>
                            {documentFiles.length > 0 ? (
                                <div className={styles.documentList}>
                                    {documentFiles.filter(doc => doc.fileElem?.sourceUrl).map(doc => (
                                        <div key={doc.clientMsgID} className={styles.documentItem} onClick={() => downloadFile(doc)}>
                                            <div className={styles.documentIcon}>
                                                <FileText size={20} />
                                            </div>
                                            <div className={styles.documentInfo}>
                                                <div className={styles.documentName}>{doc.fileElem?.fileName || 'File'}</div>
                                                <div className={styles.documentDetails}>
                                                    <span className={styles.documentSize}>
                                                        {formatFileSize(doc.fileElem?.fileSize || 0)}
                                                    </span>
                                                    <span className={styles.documentDate}>
                                                        {formatTimestamp(doc.createTime)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button className={styles.downloadBtn} onClick={(e) => { e.stopPropagation(); downloadFile(doc); }}>
                                                <Download size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <p>{t.noDocumentsShared}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* User Detail Popup */}
            {userType !== UserType.Customer && selectedUser && (
                <div className={`${styles.popupOverlay} ${styles.show}`} onClick={(e) => { e.stopPropagation(); setSelectedUser(null); }}>
                    <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closePopupBtn} onClick={(e) => { e.stopPropagation(); setSelectedUser(null); }}>&times;</button>

                        <div className={styles.popupHeader}>
                            <div className={styles.popupAvatarWrapper}>
                                {selectedUser.faceURL ? (
                                    <img src={selectedUser.faceURL} alt={selectedUser.nickname} className={styles.popupAvatar} />
                                ) : (
                                    <div
                                        className={styles.popupAvatarInitials}
                                        style={{ backgroundColor: getAvatarColor(selectedUser.nickname || selectedUser.userID) }}
                                    >
                                        {getInitials(selectedUser.nickname || selectedUser.userID)}
                                    </div>
                                )}
                            </div>
                            <h3 className={styles.popupName}>{selectedUser.nickname || selectedUser.userID}</h3>
                            {selectedUser.type && (
                                <span className={styles.popupBadge}>{selectedUser.type}</span>
                            )}
                        </div>

                        <div className={styles.popupDivider} />

                        <div className={styles.popupContactInfo}>
                            <div className={styles.popupContactItem}>
                                <div className={styles.popupContactIcon}><Mail size={18} /></div>
                                <div className={styles.popupContactDetails}>
                                    <span className={styles.popupContactLabel}>{t.email}</span>
                                    <span className={styles.popupContactValue}>{selectedUser.email || t.notProvided}</span>
                                </div>
                            </div>
                            <div className={styles.popupContactItem}>
                                <div className={styles.popupContactIcon}><Phone size={18} /></div>
                                <div className={styles.popupContactDetails}>
                                    <span className={styles.popupContactLabel}>{t.phone}</span>
                                    <span className={styles.popupContactValue}>{selectedUser.phone || t.notProvided}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}
